import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiResponse, Product } from '@shared/models';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { WindowService } from '@shared/window';

@Injectable({
    providedIn: 'root',
})
export class HttpService {
    private httpClient = inject(HttpClient);
    private translateService = inject(TranslateService);
    private windowService = inject(WindowService);

    getProducts(limit = 30, page = 1, categoriesIds: string[] | null, search: string): Observable<{ products: Product[]; total: number }> {
        if (isNaN(page) || page < 1) page = 1;

        let params: HttpParams = new HttpParams().append('limit', limit.toString()).append('page', page.toString());
        if (categoriesIds) {
            params = params.append('categories', categoriesIds.join(';'));
        }

        if (search) {
            params = params.append('search', search);
        }

        const url = params.has('search') || params.has('categories') ? 'api/public/products' : 'api/v2/public/products';

        return forkJoin([
            this.httpClient.get<ApiResponse<Product[]>>(url, { params }).pipe(
                map((results) => {
                    return {
                        products: [...results.data],
                        total: results.pagination?.totalCount,
                    };
                }),
            ),
            this.httpClient
                .skipErrorHandler()
                .get(`api/public/products-classic/csv/${this.translateService.currentLang}`, {
                    observe: 'response',
                    responseType: 'blob',
                })
                .pipe(
                    map((response) => response.body),
                    filter((body) => body !== null),
                    mergeMap((body) => this.csvToArray(body)),
                    catchError(() => {
                        return [];
                    }),
                ),
        ]).pipe(
            map(([data, additionalProducts]) => {
                const additionalProductsFiltered = categoriesIds
                    ? additionalProducts.filter((product) => product.categories.map((category) => category.id).includes(categoriesIds[0]))
                    : [...additionalProducts];

                const total = (data.total ?? 0) + additionalProductsFiltered.length;
                const shown = limit * page;
                const missing = shown - (data.total ?? 0);

                if (missing >= 0) {
                    const start = missing - limit < 0 ? 0 : missing - limit;
                    const endLimit = limit - (limit - missing < 0 ? 0 : limit - missing);
                    const totalProducts = [...data.products, ...additionalProductsFiltered.splice(start, endLimit)];
                    return {
                        products: totalProducts,
                        total: total,
                    };
                }

                return {
                    products: data.products,
                    total: total,
                };
            }),
        );
    }

    csvToArray(csvFile: Blob): Observable<Product[]> {
        if (this.windowService.isBrowser) {
            const fileReader = new FileReader();
            fileReader.readAsText(csvFile);

            return new Observable((observer): void => {
                fileReader.onload = (e) => {
                    const csvString = <string>e.target?.result;
                    const response = csvString.split('\n').map(function (line) {
                        return line.split(';');
                    });
                    response.shift();
                    const products = response
                        .filter((response) => response.length === 6)
                        .map(([category, stelmachId, width, height, weight, imageUrl]) => ({
                            id: stelmachId,
                            slug: stelmachId,
                            name: stelmachId,
                            description: '',
                            patternName: stelmachId,
                            image: {
                                url: imageUrl,
                            },
                            categories: [
                                {
                                    id: category,
                                    name: category,
                                    slug: category,
                                    description: '',
                                    featured: false,
                                    image: {
                                        url: imageUrl,
                                    },
                                },
                            ],
                            parameters: {
                                height: height?.slice(0, -2) || '',
                                height_z_kam: height?.slice(0, -2) || '',
                                width: width?.slice(0, -2) || '',
                                width_z_kam: width?.slice(0, -2) || '',
                                stone_label: '',
                                default_ct_count: '',
                                weight: weight,
                            },
                        }));
                    observer.next(products);
                    observer.complete();
                };

                fileReader.onerror = (error) => {
                    observer.error(error);
                };
            });
        }
        return of([]);
    }
}
